import React, { useEffect, useState, useCallback } from "react";
import { Popconfirm, Space, Table, Tag } from "antd";
import { EditTwoTone, DeleteTwoTone, EyeTwoTone, MailOutlined, PhoneOutlined, HomeOutlined, EnvironmentOutlined, UserOutlined, GlobalOutlined, NumberOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SubHeader from "../../components/SubHeader/SubHeader";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomModal from "../../components/CustomModal/CustomModal";
import Loader from "../../components/Loader/Loader";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi, getApi } from "../../utils/apiService";
import {
    GET_ALL_BRANCH,
    DELETE_BRANCH,
    GET_BRANCH_BY_ID
} from "../../utils/apiPath";
import "./ManageBranch.scss";

const ManageBranch = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [branchList, setBranchList] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingBranch, setViewingBranch] = useState(null);

    const fetchBranchList = useCallback(async () => {
        setIsLoading(true);
        const { statusCode, data, message } = await getApi(GET_ALL_BRANCH);
        if (statusCode === 200) {
            setBranchList(data || []);
            setTotalRecords(data?.length || 0);
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    }, [pageIndex, pageSize]);

    useEffect(() => {
        fetchBranchList();
    }, [fetchBranchList]);

    const handlePageChange = (page, size) => {
        setPageIndex(page - 1);
        setPageSize(size);
    };

    const handleDelete = async (branchId) => {
        setIsLoading(true);
        const { statusCode, message } = await postApi(DELETE_BRANCH, { branchId });
        if (statusCode === 200) {
            successToast(message);
            fetchBranchList();
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    const handleViewDetails = async (record) => {
        setIsLoading(true);
        const { statusCode, data, message } = await postApi(GET_BRANCH_BY_ID, { branchId: record.branchId });
        if (statusCode === 200) {
            setViewingBranch(data);
            setIsViewModalOpen(true);
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    const columns = [
        {
            title: "S.No",
            key: "sno",
            width: 70,
            align: "center",
            render: (_, __, index) => pageIndex * pageSize + (index + 1)
        },
        {
            title: "Branch Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: "Branch Code",
            dataIndex: "code",
            key: "code",
            align: "center",
            render: (code) => <Tag color="blue">{code}</Tag>
        },
        {
            title: "City",
            dataIndex: "city",
            key: "city"
        },
        {
            title: "Email",
            dataIndex: "emailId",
            key: "emailId"
        },
        {
            title: "Phone",
            dataIndex: "phoneNumber",
            key: "phoneNumber"
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <ActionGroup>
                    <ActionIcon
                        title="View Details"
                        icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                        onClick={() => handleViewDetails(record)}
                    />
                    <ActionIcon
                        title="Edit"
                        icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                        onClick={() => navigate(`/edit-branch/${record.branchId}`)}
                    />
                    <ActionIcon
                        title="Delete"
                        icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                        confirm={{
                            enabled: true,
                            title: `Delete "${record.name}"?`,
                            okText: "Delete",
                            cancelText: "Cancel",
                            onConfirm: () => handleDelete(record.branchId),
                        }}
                    />
                </ActionGroup>
            )
        }
    ];

    return (
        <div className="manage-branch">
            {isLoading && <Loader />}
            <SubHeader
                title="Manage Branch"
                showBack={false}
                showRight={true}
                rightActionLabel="Add New Branch"
                onRightClick={() => navigate("/add-branch")}
            />

            <div className="table-container">
                <CustomTable
                    columns={columns}
                    dataSource={branchList}
                    total={totalRecords}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    rowKey="branchId"
                />
            </div>

            <CustomModal
                open={isViewModalOpen}
                title="Branch Details"
                onClose={() => setIsViewModalOpen(false)}
                width={800}
                showPrimary={false}
                dangerText="Close"
            >
                {viewingBranch && (
                    <div className="branch-details-view">
                        <div className="details-header">
                            <div className="header-icon">
                                <HomeOutlined />
                            </div>
                            <div className="header-info">
                                <h3>{viewingBranch.name}</h3>
                                <p className="code">Branch Code: <span>{viewingBranch.code}</span></p>
                                <Tag color="blue">Principal: {viewingBranch.principalName}</Tag>
                            </div>
                        </div>

                        <div className="details-grid">
                            <div className="detail-item full-width">
                                <label><EnvironmentOutlined /> Address</label>
                                <span>{viewingBranch.address}</span>
                            </div>
                            <div className="detail-item">
                                <label><GlobalOutlined /> City & State</label>
                                <span>{viewingBranch.city}, {viewingBranch.state}</span>
                            </div>
                            <div className="detail-item">
                                <label><EnvironmentOutlined /> Pincode</label>
                                <span>{viewingBranch.pincode}</span>
                            </div>
                            <div className="detail-item">
                                <label><MailOutlined /> Email</label>
                                <span>{viewingBranch.emailId}</span>
                            </div>
                            <div className="detail-item">
                                <label><PhoneOutlined /> Phone</label>
                                <span>{viewingBranch.phoneNumber}</span>
                            </div>
                        </div>

                        {viewingBranch.branchAdmin && (
                            <div className="admin-section">
                                <h4><UserOutlined /> Branch Admin Details</h4>
                                <div className="details-grid">
                                    <div className="detail-item">
                                        <label>Admin Name</label>
                                        <span>{viewingBranch.branchAdmin.userName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Admin Email</label>
                                        <span>{viewingBranch.branchAdmin.emailId}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Admin Phone</label>
                                        <span>{viewingBranch.branchAdmin.phoneNumber}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </CustomModal>
        </div>
    );
};

export default ManageBranch;
